import { useRef } from 'react'
import { LandingHeader } from './LandingHeader'
import { LandingFooter } from './LandingFooter'
import { SvgViewer } from './SvgViewer'
import { playgroundUrl } from './playgroundUrl'

const enSource = `customer
do: place order
needs: cart, payment method
yields: order
at: web

fraud service
do: score risk
needs: order, customer history
yields: risk score
at: payments

payment service
do: authorize payment
needs: order
yields: authorized order, charge id
at: payments

payment service
do: capture payment
needs: authorized order, risk score
yields: paid order
at: payments

warehouse
do: pick items
needs: paid order
yields: picked package
at: fulfillment

warehouse
do: ship package
needs: picked package, shipping label
yields: shipped order
at: fulfillment

notifier
do: send receipt
needs: paid order, email address
yields: receipt email
at: comms

support
do: process refund
needs: shipped order, customer complaint, charge id
yields: refunded
at: support`

// Patched variant for the equivalent tool's "after" SvgViewer —
// same checkout minus the fraud service, and capture payment no
// longer needs `risk score`. Clicking the playground link should
// open the patched version so a reader can run structure / live /
// invariant on it themselves.
const enSourceNoFraud = `customer
do: place order
needs: cart, payment method
yields: order
at: web

payment service
do: authorize payment
needs: order
yields: authorized order, charge id
at: payments

payment service
do: capture payment
needs: authorized order
yields: paid order
at: payments

warehouse
do: pick items
needs: paid order
yields: picked package
at: fulfillment

warehouse
do: ship package
needs: picked package, shipping label
yields: shipped order
at: fulfillment

notifier
do: send receipt
needs: paid order, email address
yields: receipt email
at: comms

support
do: process refund
needs: shipped order, customer complaint, charge id
yields: refunded
at: support`

// Each tool's section has a sticky LEFT column + scrolling RIGHT column
// on desktop. On mobile the sections stack: tool meta (number, title,
// concept) appears FIRST, then the visual, then extras/response/insight.
// The ToolSection component below handles both orderings from one
// declaration — see its render for how the visual is placed via sticky
// on desktop vs inline-below-meta on mobile.
//
// Five unique SVGs total across landing + tools:
//
//   hero-diagram.svg          full checkout · all overlays     landing demo
//   checkout-structure.svg    extracted front half (4 actions) structure tool
//   checkout-plain.svg        full checkout · no overlays      equivalent · before
//   checkout-no-fraud.svg     patched checkout (no fraud)      equivalent · after
//   checkout-payments.svg     extracted payments subsystem     compose tool

// ─── Tool data ──────────────────────────────────────────────

interface Tool {
    name: string
    question: string
    field: string
    concept: string
    call: string
    response: string
    insight: string
}

const tools: Tool[] = [
    {
        name: 'structure',
        question: 'What is this system?',
        field: 'Topology, bridges, subsystems',
        concept:
            'What shape does your system take — pipeline, fork-join, cycle, or something more complex? ' +
            'Where are the single points of failure — the nodes every flow has to pass through? ' +
            'And where do natural groupings emerge — clusters of tightly-coupled work you could peel ' +
            'off as separate services? This tool answers all three from the graph alone, without you ' +
            'drawing any boundaries.',
        call: 'structure({ source })',
        response:
            'Complex Topology | 8 actions · 15 entities · 6 actors · 5 locations\n' +
            'depth 12 · critical path 13 · max parallelism 6\n' +
            '\n' +
            'Subsystems discovered (4)\n' +
            '  1  intake        {place order · score risk}\n' +
            '  2  auth/refund   {authorize payment · process refund}\n' +
            '  3  settle        {capture payment · send receipt}\n' +
            '  4  fulfillment   {pick items · ship package}\n' +
            '\n' +
            'Bridge nodes (7)\n' +
            '  order · paid order · place order · score risk\n' +
            '  send receipt · ship package · process refund\n' +
            '\n' +
            'Min-cut (12 source→sink pairs affected, cut size 1)\n' +
            '  place order · score risk · send receipt · ship package · process refund\n' +
            '\n' +
            'Interface nodes (cross-subsystem bridges)\n' +
            '  capture payment   spans 3 subsystems\n' +
            '  authorized order · pick items · risk score · shipped order   span 2 each',
        insight:
            'If fraud scoring goes down, every order stops. Not just risky ones. Every checkout ' +
            'funnels through the same check, so one outage blocks 100% of traffic. And the engine ' +
            'found four natural service boundaries on its own — intake, payments/refund, settlement, ' +
            'fulfillment — each a candidate for its own team and deployment.',
    },
    {
        name: 'invariant',
        question: "What's always true?",
        field: 'Conservation laws · depletion risks',
        concept:
            'Some things about your system hold no matter what happens next. Like "every cart consumes ' +
            'exactly one payment method", or "every customer complaint is eventually balanced by a refund." ' +
            'These aren\'t enforced by code — they fall out of the shape of the needs/yields graph. ' +
            'The tool finds them automatically. Useful for spec sanity-checks and for catching unintended ' +
            'consumption patterns before they ship.',
        call: 'invariant({ source })',
        response:
            'Conservation Laws (3)\n' +
            '  payment method − cart = 0            (every cart consumes exactly one payment)\n' +
            '  customer complaint + refunded = 0    (complaints balance refunds)\n' +
            '  email address + receipt email = 0    (every email in produces one receipt)\n' +
            '\n' +
            'Depletable sets (6) — groups whose joint depletion is irreversible\n' +
            '  {customer complaint}\n' +
            '  {payment method}\n' +
            '  {shipping label}\n' +
            '  {customer history, risk score}\n' +
            '  {cart, charge id, order, refunded}\n' +
            '  {authorized order, cart, order, paid order, picked package, shipped order}\n' +
            '\n' +
            'Deficiency 0   (structure fully determines dynamics)',
        insight:
            'Every cart that enters consumes exactly one payment. Every complaint is balanced by exactly ' +
            'one refund. Every email in produces one receipt. These are guaranteed by the shape of the ' +
            'needs/yields graph — nothing in the code can violate them without editing the EN itself.',
    },
    {
        name: 'live',
        question: 'Can it deadlock? Can it overflow?',
        field: 'Liveness · boundedness',
        concept:
            'A system is "live" if nothing can permanently stall. The tool finds two failure modes. ' +
            'First, drain traps — groups of entities that, once emptied, stay empty forever because ' +
            'nothing in the system refills them. (Think: your notification queue fills up, drains, ' +
            'and never gets replenished.) Second, overflow risks — cycles that could produce more ' +
            'than they consume and pile up without limit. Both are structural — caught without ever ' +
            'running the system.',
        call: 'live({ source })',
        response:
            'isStructurallyLive: false    (7 siphons · 2 covered · 5 uncovered)\n' +
            '\n' +
            'Drain traps (uncovered siphons)\n' +
            '  {customer complaint}                    affects: support\n' +
            '  {payment method}                        affects: customer\n' +
            '  {shipping label}                        affects: warehouse\n' +
            '  {customer history, risk score}          affects: fraud service, payment service\n' +
            '  {authorized order, cart, order, paid order, picked package, shipped order}\n' +
            '      affects: every actor across every location\n' +
            '\n' +
            'isStructurallyBounded: false   (6 per-action accumulators could grow unbounded)',
        insight:
            'The refund flow only fires when customers complain. If the complaint channel goes quiet, ' +
            'refunds stop with it. And the system has six external inputs whose loss stalls everything ' +
            'downstream. Worth knowing before the next release that changes how those inputs are captured.',
    },
    {
        name: 'reachable',
        question: 'Can X reach Y?',
        field: 'Path tracing · coverage',
        concept:
            'Given two points in your system, is there a path between them? This tool traces every ' +
            'step of the shortest path: who performs each action, where it runs, which locations it ' +
            'crosses. Useful for answering "can a refund request eventually produce a cancelled shipment?" ' +
            'or "does every payment pass through fraud scoring?" — without tracing the graph by hand.',
        call: 'reachable({ source, from: "cart", to: "refunded" })',
        response:
            'reachable: true · distance: 6 hops · crossings: web → payments → support\n' +
            '\n' +
            'Shortest path\n' +
            '  cart                   SOURCE\n' +
            '  → place order          JOIN      customer         @ web\n' +
            '  → order\n' +
            '  → authorize payment    FORK      payment service  @ payments\n' +
            '  → charge id\n' +
            '  → process refund       JOIN      support          @ support\n' +
            '  → refunded',
        insight:
            'A refund can fire without the order ever shipping. The fastest route goes straight from ' +
            'authorization to refund via the charge id — warehouse and courier aren\'t on the refund ' +
            'path. Useful when you write rules for pre-ship cancellation flows.',
    },
    {
        name: 'equivalent',
        question: 'What changed?',
        field: 'Structural diff · dry-run',
        concept:
            'What changes if you drop a service, rename an action, or add a new step? This tool compares ' +
            'two versions of the same system and shows exactly what moved: which nodes appeared or ' +
            'disappeared, which subsystems got reshuffled, which roles changed, how many edit operations ' +
            'separate the two. Dry-run a refactor here before you ship it.',
        call: 'equivalent({ source_a: with-fraud, source_b: without-fraud })',
        response:
            'Compare: with-fraud (8 actions)  vs  without-fraud (7 actions)\n' +
            '\n' +
            '  shapeBefore:       Complex Topology\n' +
            '  shapeAfter:        Complex Topology\n' +
            '  shapeChanged:      false\n' +
            '\n' +
            '  nodesOnlyInA:      [score risk]\n' +
            '  entitiesOnlyInA:   [customer history, risk score]\n' +
            '  roleChanges:       [capture payment: JOIN → —]\n' +
            '\n' +
            '  subsystemChanges (6 nodes reassigned)\n' +
            '    authorize payment   Subsystem 2 → Subsystem 4\n' +
            '    capture payment     Subsystem 3 → Subsystem 2\n' +
            '    pick items          Subsystem 4 → Subsystem 3\n' +
            '    ship package        Subsystem 4 → Subsystem 3\n' +
            '    send receipt        Subsystem 3 → Subsystem 2\n' +
            '    process refund      Subsystem 2 → Subsystem 4\n' +
            '\n' +
            '  editDistance:      6 ops   (3 node deletions · 3 edge deletions)\n' +
            '  stageCount  A / B: 15 / 14',
        insight:
            'Dropping fraud scoring rewires the whole system. The edit is six operations, but six ' +
            'nodes also get reassigned to different subsystems — the service boundaries shift under your ' +
            'feet. A refactor that looks tiny in the diff can move a lot of ownership lines.',
    },
    {
        name: 'compose',
        question: 'How do parts combine?',
        field: 'Merge · extract',
        concept:
            'Systems are made of parts. Extract pulls out a subsystem as a clean, standalone EN spec — ' +
            'boundary inputs and outputs auto-derived, runnable on its own through any other tool. ' +
            'Merge stitches two systems together by matching shared entities. Useful for handing a service ' +
            'off to another team, modelling pieces of an org independently, or composing a big system ' +
            'from smaller ones.',
        call: 'compose({ source, subsystem: "Subsystem 4" })',
        response:
            'Subsystem 4 (fulfillment)\n' +
            '  Boundary inputs:    paid order, shipping label\n' +
            '  Boundary outputs:   shipped order\n' +
            '  Internal entities:  picked package\n' +
            '  Actors: warehouse · Location: fulfillment\n' +
            '\n' +
            '# Extracted EN\n' +
            'warehouse\n' +
            'do: pick items\n' +
            'needs: paid order\n' +
            'yields: picked package\n' +
            'at: fulfillment\n' +
            '\n' +
            'warehouse\n' +
            'do: ship package\n' +
            'needs: picked package, shipping label\n' +
            'yields: shipped order\n' +
            'at: fulfillment',
        insight:
            'Pull fulfillment out as its own service. Two actions, two inputs (paid order + shipping label), ' +
            'one output (shipped order). That\'s the contract. Hand it to the warehouse team without ' +
            'drafting it by hand — the inputs and outputs are derived mechanically from where the ' +
            'subsystem meets the rest of the system.',
    },
]

// ─── Components ─────────────────────────────────────────────

/** Uniform output block: header shows the MCP call signature on the
 *  left, a small "response" tag on the right, then the raw output in
 *  the body. Used for every tool so the visual rhythm of "call →
 *  response" is consistent across the page. */
function OutputBlock({ call, children }: { call: string; children: string }) {
    return (
        <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border)' }}>
            <div
                className="flex items-center justify-between gap-4 px-3 py-2 bg-surface-elevated"
                style={{ borderBottom: '1px solid var(--border-subtle)' }}
            >
                <code className="text-[11px] font-mono text-accent truncate">{call}</code>
                <span className="text-[10px] font-mono text-text-muted uppercase tracking-wider shrink-0">
                    response
                </span>
            </div>
            <pre className="px-4 py-3 font-mono text-[11px] sm:text-[13px] leading-[1.6] sm:leading-[1.8] text-text-secondary whitespace-pre-wrap overflow-x-auto">
                {children}
            </pre>
        </div>
    )
}

/** Compact terminal-chrome source panel — used for the equivalent
 *  tool's before/after comparison. Diff-style highlights:
 *    removedLines    lines only present in this version but not the
 *                    other (rendered red, struck-through)
 *    modifiedLines   lines that changed between versions (rendered
 *                    in accent color, subtly tinted)
 *  Both lists are 0-indexed against the source after split('\n'). */
function SourceChip({
    title, source, removedLines, modifiedLines,
}: {
    title: string
    source: string
    removedLines?: number[]
    modifiedLines?: number[]
}) {
    const lines = source.split('\n')
    const removedSet = new Set(removedLines ?? [])
    const modifiedSet = new Set(modifiedLines ?? [])
    return (
        <div className="rounded-lg overflow-hidden flex flex-col" style={{ border: '1px solid var(--border)' }}>
            <div
                className="flex items-center gap-2 px-3 py-1.5 bg-surface-elevated flex-shrink-0"
                style={{ borderBottom: '1px solid var(--border-subtle)' }}
            >
                <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full" style={{ background: 'color-mix(in srgb, var(--danger) 50%, transparent)' }} />
                    <div className="w-2 h-2 rounded-full" style={{ background: 'color-mix(in srgb, var(--warning) 50%, transparent)' }} />
                    <div className="w-2 h-2 rounded-full" style={{ background: 'color-mix(in srgb, var(--success) 50%, transparent)' }} />
                </div>
                <span className="text-[10px] font-mono text-text-muted">{title}</span>
            </div>
            <pre className="p-3 font-mono text-[10px] leading-[1.55] text-text-secondary overflow-auto flex-1 min-h-0 max-h-[320px]">
                {lines.map((line, i) => {
                    const removed = removedSet.has(i)
                    const modified = modifiedSet.has(i)
                    const style = removed
                        ? {
                            background: 'color-mix(in srgb, var(--danger) 14%, transparent)',
                            color: 'var(--danger)',
                            textDecoration: 'line-through',
                            textDecorationColor: 'color-mix(in srgb, var(--danger) 60%, transparent)',
                        }
                        : modified
                        ? {
                            background: 'color-mix(in srgb, var(--accent) 10%, transparent)',
                            color: 'var(--accent)',
                        }
                        : undefined
                    return (
                        <div key={i} style={style}>
                            {line || '\u00a0'}
                        </div>
                    )
                })}
            </pre>
        </div>
    )
}

/** A single tool section with a responsive layout:
 *
 *  Desktop (lg+): two-column grid. Left column sticks while you scroll,
 *  holding the tool's visual artifact (SVG, response block, or source
 *  comparison — whatever earns the sticky slot for that tool). Right
 *  column scrolls through meta (number + title + field + concept),
 *  optional extras, optional response block, and the insight card.
 *
 *  Mobile: single column. Tool meta renders first so the reader sees
 *  "what this tool is" before "what it output". The visual follows the
 *  meta, then extras/response/insight.
 *
 *  Two SvgViewer instances are rendered (one per breakpoint wrapper),
 *  but only one is visible at a time via Tailwind `hidden lg:block` /
 *  `lg:hidden`. Each instance manages its own zoom state, which is
 *  fine since the user never sees both simultaneously. */
function ToolSection({
    number, tool, visual, extras, showResponse, isLast,
    sectionRef,
}: {
    number: number
    tool: Tool
    visual: React.ReactNode
    extras?: React.ReactNode
    showResponse: boolean
    isLast: boolean
    sectionRef?: (el: HTMLElement | null) => void
}) {
    const meta = (
        <>
            <div className="flex items-baseline gap-3 mb-2">
                <span className="font-mono text-xs text-text-muted">{String(number).padStart(2, '0')}</span>
                <code className="text-lg font-mono text-accent font-bold">{tool.name}</code>
            </div>
            <h2 className="font-display text-xl md:text-2xl font-bold mb-1">
                {tool.question}
            </h2>
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-text-muted mb-5">
                {tool.field}
            </p>
            <p className="text-text-secondary text-sm leading-relaxed mb-6">
                {tool.concept}
            </p>
        </>
    )
    const insight = (
        <div className="rounded-lg p-4 flex items-start gap-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <div className="shrink-0 w-6 h-6 rounded-full bg-accent flex items-center justify-center text-bg text-[10px] font-bold mt-0.5">
                AI
            </div>
            <div className="min-w-0">
                <p className="italic text-text-secondary text-sm leading-relaxed">
                    &ldquo;{tool.insight}&rdquo;
                </p>
                <p className="text-text-muted text-[10px] mt-2 font-mono">
                    — interpreting engine output
                </p>
            </div>
        </div>
    )

    return (
        <section
            ref={sectionRef}
            data-tool={tool.name}
            className="lg:grid lg:grid-cols-[1fr_1fr] lg:gap-12 py-12"
            style={!isLast ? { borderBottom: '1px solid var(--border-subtle)' } : undefined}
        >
            {/* Desktop-only: sticky visual on the left. Hidden on mobile
                because the mobile ordering renders a duplicate visual
                inline after the meta. */}
            <div className="hidden lg:block lg:sticky lg:top-24 lg:self-start">
                {visual}
            </div>

            {/* Right column on desktop; only column on mobile. */}
            <div className="space-y-5">
                {meta}
                {/* Mobile-only inline visual, positioned between meta and
                    extras/response. Desktop has the sticky version above. */}
                <div className="lg:hidden">{visual}</div>
                {extras}
                {showResponse && (
                    <OutputBlock call={tool.call}>{tool.response}</OutputBlock>
                )}
                {insight}
            </div>
        </section>
    )
}

// ─── Page ───────────────────────────────────────────────────

export function ToolsPage() {
    const tryUrl = playgroundUrl
    const sectionRefs = useRef<Record<string, HTMLElement | null>>({})


    return (
        <div className="min-h-screen bg-bg" style={{ scrollBehavior: 'smooth' }}>
            <LandingHeader />

            <main className="pt-32 pb-24 px-4 sm:px-6">
                <div className="max-w-6xl mx-auto">

                    {/* Hero — two-column: header copy on the left,
                        scrollable source panel on the right. Aligns the
                        two to the same height so the page opens with a
                        single visually-coherent block. Source is long
                        (7 actions, ~35 lines) so it scrolls inside its
                        own panel instead of pushing the rest of the page
                        down. */}
                    <div className="mb-20 lg:grid lg:grid-cols-2 lg:gap-12 lg:items-start">
                        <div>
                            <p className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-4">
                                Six tools. Six structural questions.
                            </p>
                            <h1 className="font-display text-3xl md:text-5xl font-bold leading-[1.1] mb-6">
                                Concurrency theory,<br />
                                <span className="text-accent">packaged as callable tools.</span>
                            </h1>
                            <p className="text-text-secondary leading-relaxed mb-8">
                                Topology, invariants, liveness, reachability, equivalence,
                                composition &mdash; six questions every concurrent system must answer.
                                The math has existed for decades; EN makes it callable with one MCP
                                request.
                            </p>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                <a
                                    href="/#get-started"
                                    className="bg-accent text-bg font-bold px-6 py-3 rounded-full text-sm hover:opacity-90 transition-opacity"
                                >
                                    Install MCP Server
                                </a>
                                <a
                                    href={tryUrl}
                                    className="font-medium px-6 py-3 rounded-full text-sm text-text hover:bg-surface transition-colors"
                                    style={{ border: '1px solid var(--border)' }}
                                >
                                    Open in Playground
                                </a>
                            </div>
                        </div>
                        <div className="mt-8 lg:mt-0 rounded-xl overflow-hidden flex flex-col lg:h-[520px]" style={{ border: '1px solid var(--border)' }}>
                            <div
                                className="flex items-center gap-3 px-4 py-2.5 bg-surface-elevated flex-shrink-0"
                                style={{ borderBottom: '1px solid var(--border-subtle)' }}
                            >
                                <div className="flex gap-1.5">
                                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'color-mix(in srgb, var(--danger) 50%, transparent)' }} />
                                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'color-mix(in srgb, var(--warning) 50%, transparent)' }} />
                                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: 'color-mix(in srgb, var(--success) 50%, transparent)' }} />
                                </div>
                                <span className="text-[11px] font-mono text-text-muted">checkout.en</span>
                            </div>
                            <pre className="p-5 font-mono text-[11px] sm:text-[13px] leading-[1.6] sm:leading-[1.8] flex-1 min-h-0 overflow-auto">
                                <div className="text-accent font-medium">customer</div>
                                <div><span className="text-text-secondary">do: </span><span className="text-text">place order</span></div>
                                <div><span className="text-text-secondary">needs: </span><span className="text-text">cart, payment method</span></div>
                                <div><span className="text-text-secondary">yields: </span><span className="text-text">order</span></div>
                                <div><span className="text-text-secondary">at: </span><span className="text-text-muted">web</span></div>
                                <div className="h-3" />
                                <div className="text-accent font-medium">fraud service</div>
                                <div><span className="text-text-secondary">do: </span><span className="text-text">score risk</span></div>
                                <div><span className="text-text-secondary">needs: </span><span className="text-text">order, customer history</span></div>
                                <div><span className="text-text-secondary">yields: </span><span className="text-text">risk score</span></div>
                                <div><span className="text-text-secondary">at: </span><span className="text-text-muted">payments</span></div>
                                <div className="h-3" />
                                <div className="text-accent font-medium">payment service</div>
                                <div><span className="text-text-secondary">do: </span><span className="text-text">authorize payment</span></div>
                                <div><span className="text-text-secondary">needs: </span><span className="text-text">order</span></div>
                                <div><span className="text-text-secondary">yields: </span><span className="text-text">authorized order, charge id</span></div>
                                <div><span className="text-text-secondary">at: </span><span className="text-text-muted">payments</span></div>
                                <div className="h-3" />
                                <div className="text-accent font-medium">payment service</div>
                                <div><span className="text-text-secondary">do: </span><span className="text-text">capture payment</span></div>
                                <div><span className="text-text-secondary">needs: </span><span className="text-text">authorized order, risk score</span></div>
                                <div><span className="text-text-secondary">yields: </span><span className="text-text">paid order</span></div>
                                <div><span className="text-text-secondary">at: </span><span className="text-text-muted">payments</span></div>
                                <div className="h-3" />
                                <div className="text-accent font-medium">warehouse</div>
                                <div><span className="text-text-secondary">do: </span><span className="text-text">pick items</span></div>
                                <div><span className="text-text-secondary">needs: </span><span className="text-text">paid order</span></div>
                                <div><span className="text-text-secondary">yields: </span><span className="text-text">picked package</span></div>
                                <div><span className="text-text-secondary">at: </span><span className="text-text-muted">fulfillment</span></div>
                                <div className="h-3" />
                                <div className="text-accent font-medium">warehouse</div>
                                <div><span className="text-text-secondary">do: </span><span className="text-text">ship package</span></div>
                                <div><span className="text-text-secondary">needs: </span><span className="text-text">picked package, shipping label</span></div>
                                <div><span className="text-text-secondary">yields: </span><span className="text-text">shipped order</span></div>
                                <div><span className="text-text-secondary">at: </span><span className="text-text-muted">fulfillment</span></div>
                                <div className="h-3" />
                                <div className="text-accent font-medium">notifier</div>
                                <div><span className="text-text-secondary">do: </span><span className="text-text">send receipt</span></div>
                                <div><span className="text-text-secondary">needs: </span><span className="text-text">paid order, email address</span></div>
                                <div><span className="text-text-secondary">yields: </span><span className="text-text">receipt email</span></div>
                                <div><span className="text-text-secondary">at: </span><span className="text-text-muted">comms</span></div>
                                <div className="h-3" />
                                <div className="text-accent font-medium">support</div>
                                <div><span className="text-text-secondary">do: </span><span className="text-text">process refund</span></div>
                                <div><span className="text-text-secondary">needs: </span><span className="text-text">shipped order, customer complaint, charge id</span></div>
                                <div><span className="text-text-secondary">yields: </span><span className="text-text">refunded</span></div>
                                <div><span className="text-text-secondary">at: </span><span className="text-text-muted">support</span></div>
                            </pre>
                        </div>
                    </div>

                    {/* Tool sections — each is a sticky-left / scroll-right pair.
                        Left column content varies per tool:
                          structure  → SVG of the extracted front half
                          invariant  → raw response block (the picture IS the output)
                          live       → raw response block
                          reachable  → raw response block
                          equivalent → full-width 2x2 compare block (before/after
                                       sources left, SVGs right); no sticky
                          compose    → SVG of the extracted subsystem
                        Response on the right column is skipped for the three
                        tools whose response already lives on the left. */}
                    <div>
                        {tools.map((tool, i) => {
                            const isLast = i === tools.length - 1
                            const sectionRef = (el: HTMLElement | null) => { sectionRefs.current[tool.name] = el }

                            // Each tool picks what goes in the sticky-left slot
                            // (on desktop) / below-meta slot (on mobile), plus
                            // whether its response block should render on the
                            // right (desktop) / below-visual (mobile) too.

                            if (tool.name === 'structure') {
                                return (
                                    <ToolSection
                                        key={tool.name}
                                        sectionRef={sectionRef}
                                        number={i + 1}
                                        tool={tool}
                                        isLast={isLast}
                                        showResponse
                                        visual={
                                            <SvgViewer
                                                src="/examples/checkout-structure.svg"
                                                label="Extracted front half · place order → capture"
                                                alt="Checkout front half structural view"
                                                maxHeight="520px"
                                            />
                                        }
                                    />
                                )
                            }
                            if (tool.name === 'compose') {
                                return (
                                    <ToolSection
                                        key={tool.name}
                                        sectionRef={sectionRef}
                                        number={i + 1}
                                        tool={tool}
                                        isLast={isLast}
                                        showResponse
                                        visual={
                                            <SvgViewer
                                                src="/examples/checkout-payments.svg"
                                                label="Extracted payments subsystem · a standalone EN spec"
                                                alt="Extracted payments subsystem"
                                                maxHeight="520px"
                                            />
                                        }
                                    />
                                )
                            }
                            if (tool.name === 'equivalent') {
                                // Sources pair as the sticky visual (left on
                                // desktop, below-meta on mobile). SVG pair
                                // threads into `extras` so it lands between
                                // meta and response in the right column.
                                return (
                                    <ToolSection
                                        key={tool.name}
                                        sectionRef={sectionRef}
                                        number={i + 1}
                                        tool={tool}
                                        isLast={isLast}
                                        showResponse
                                        visual={
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {/* Before: fraud service block
                                                    (lines 6-11) is removed in
                                                    the patch; line 20
                                                    `needs: authorized order, risk score`
                                                    loses its risk-score dep. */}
                                                <SourceChip
                                                    title="checkout.en · before"
                                                    source={enSource}
                                                    removedLines={[6, 7, 8, 9, 10, 11]}
                                                    modifiedLines={[20]}
                                                />
                                                {/* After: line 14 `needs:
                                                    authorized order` is the
                                                    simplified version. */}
                                                <SourceChip
                                                    title="checkout.en · after"
                                                    source={enSourceNoFraud}
                                                    modifiedLines={[14]}
                                                />
                                            </div>
                                        }
                                        extras={
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <SvgViewer
                                                    src="/examples/checkout-plain.svg"
                                                    label="Before · with fraud service"
                                                    alt="Checkout with fraud service"
                                                    maxHeight="320px"
                                                />
                                                <SvgViewer
                                                    src="/examples/checkout-no-fraud.svg"
                                                    label="After · fraud service removed"
                                                    alt="Checkout without fraud service"
                                                    maxHeight="320px"
                                                />
                                            </div>
                                        }
                                    />
                                )
                            }
                            // invariant / live / reachable — the raw response
                            // block IS the visual. showResponse=false since
                            // rendering it in the right column too would
                            // duplicate the same block on the page.
                            return (
                                <ToolSection
                                    key={tool.name}
                                    sectionRef={sectionRef}
                                    number={i + 1}
                                    tool={tool}
                                    isLast={isLast}
                                    showResponse={false}
                                    visual={<OutputBlock call={tool.call}>{tool.response}</OutputBlock>}
                                />
                            )
                        })}
                    </div>

                </div>
            </main>

            <LandingFooter />
        </div>
    )
}
